import {useEffect, useState} from 'react'

import AddIcon from '@mui/icons-material/Add'
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import HubIcon from '@mui/icons-material/Hub'
import InputAdornment from '@mui/material/InputAdornment'
import Paper from '@mui/material/Paper'
import SearchIcon from '@mui/icons-material/Search'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import Loader from '../../shared/Loader'
import NewScope from './NewScope'
import ScopeCard from './ScopeCard'

import {OAUTH} from '../../../state/actionTypes'
import {connect} from '../../../state/hooks/tools'
import {isEmpty} from '../../../lib/tools'
import {readEndpoint} from '../../../lib/asyncMethods'
import {isSignInScope, matchesQuery, scopePermissions} from '../../../lib/oauthScopes'

import './scopesController.scss'

/**
 * The admin page for the OAuth scopes clients can ask for.
 *
 * It opens on how a scope works, because what an admin types is not what a client sends: a resource
 * becomes two permissions, requested as `resource:read` and `resource:write`. The resource scopes
 * follow, each card showing those names ready to copy, and the sign-in scopes sit apart below since
 * they carry no permissions and need no setup.
 *
 * A scope created here is called out until the notice is dismissed, so the admin can find its card
 * and the names they came to get.
 */
const STEPS = [
  {title: 'You name a resource', detail: 'Such as contacts, with a description users will understand.'},
  {title: 'Read and write come with it', detail: 'Both permissions are created for you.'},
  {title: 'Clients request each one', detail: 'As contacts:read or contacts:write.'}
]

const ScopesController = props => {
  const [showModal, setShowModal] = useState(false)
  const [query, setQuery] = useState('')
  const [createdResource, setCreatedResource] = useState(null)

  const resourceScopes = props.scopes.filter(scope => !isSignInScope(scope))
  const signInScopes = props.scopes.filter(isSignInScope)
  const visibleScopes = resourceScopes.filter(matchesQuery(query))

  useEffect(() => {
    if (isEmpty(props.scopes))
      props.readEndpoint('oauth-scopes')
  }, [])

  const toggleModal = () => {
    setShowModal(!showModal)
  }

  const handleCreated = resource => {
    setQuery('')
    setCreatedResource(resource)
  }

  return (
    <Loader loading={props.loading && isEmpty(props.scopes)}>
      <Stack className='scopes-controller'>
        <Stack direction='row' sx={{alignItems: 'center', justifyContent: 'space-between'}}>
          <Stack className='label' direction='row'>
            <HubIcon fontSize='large' />
            <Typography variant='h5'>OAuth Scopes</Typography>
          </Stack>
          <Button onClick={toggleModal} startIcon={<AddIcon />}>New Scope</Button>
        </Stack>
        <Divider />
        <Typography className='intro' variant='body2'>
          Scopes decide which parts of a user's account a connected client can reach. Clients ask for
          them by name when users sign in.
        </Typography>
        <Paper className='steps' elevation={0} sx={{bgcolor: 'Background'}}>
          {
            STEPS.map((step, index) => (
              <Stack key={step.title} className='step' direction='row'>
                <Avatar>{index + 1}</Avatar>
                <Stack>
                  <Typography variant='subtitle2'>{step.title}</Typography>
                  <Typography variant='body2'>{step.detail}</Typography>
                </Stack>
              </Stack>
            ))
          }
        </Paper>
        {
          createdResource && (
            <Alert className='notice' severity='success' onClose={() => setCreatedResource(null)}>
              <strong>{createdResource}</strong> is ready. Clients can now request the permissions on its card.
            </Alert>
          )
        }
        <Stack className='section'>
          <Stack className='toolbar' direction='row'>
            <Stack>
              <Typography className='caption' variant='caption'>Resource scopes · {resourceScopes.length}</Typography>
              <Typography variant='body2'>Each grants read and write permissions separately.</Typography>
            </Stack>
            {
              !isEmpty(resourceScopes) && (
                <TextField
                  size='small'
                  placeholder='Search scopes'
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  slotProps={{
                    htmlInput: {'aria-label': 'Search scopes'},
                    input: {startAdornment: <InputAdornment position='start'><SearchIcon /></InputAdornment>}
                  }}
                />
              )
            }
          </Stack>
          {
            visibleScopes.map(scope => (
              <ScopeCard
                key={scope.id}
                scope={scope}
                permissions={scopePermissions({resource: scope.resource, permissions: props.permissions})}
                highlighted={scope.resource === createdResource}
              />
            ))
          }
          {
            isEmpty(resourceScopes) && !props.loading && (
              <Stack className='empty'>
                <HubIcon color='disabled' />
                <Typography variant='body2'>
                  No resource scopes yet. <strong>New Scope</strong> names one, and its read and write
                  permissions come with it.
                </Typography>
              </Stack>
            )
          }
          {
            !isEmpty(resourceScopes) && isEmpty(visibleScopes) && (
              <Stack className='empty'>
                <Typography variant='body2'>No scope matches "{query}".</Typography>
              </Stack>
            )
          }
        </Stack>
        {
          !isEmpty(signInScopes) && (
            <Stack className='section'>
              <Stack>
                <Typography className='caption' variant='caption'>Sign-in scopes</Typography>
                <Typography variant='body2'>
                  Standard OpenID scopes. They identify the user, so they carry no read or write permission.
                </Typography>
              </Stack>
              <Stack className='sign-in-scopes'>
                {
                  signInScopes.map(scope => (
                    <Paper key={scope.id} elevation={0} sx={{bgcolor: 'Background'}}>
                      <Typography variant='subtitle2'>{scope.resource}</Typography>
                      <Typography variant='body2'>{scope.description}</Typography>
                    </Paper>
                  ))
                }
              </Stack>
            </Stack>
          )
        }
      </Stack>
      <NewScope showModal={showModal} toggleModal={toggleModal} onCreated={handleCreated} />
    </Loader>
  )
}

const mapStateToProps = ({oauth}) => ({scopes: [], permissions: [], ...oauth})

const actionCreators = ({oauth}) => ({readEndpoint: readEndpoint(oauth, OAUTH)})

export default connect(mapStateToProps, actionCreators)(ScopesController)
